import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, UserCheck, Users, Shield } from 'lucide-react';
import type { UsuarioVista } from '@shared/types';
import { DESCRIPCION_ROL, NOMBRE_ROL, type Rol } from '@shared/permisos';
import { FormUsuario } from '@renderer/components/forms/FormUsuario';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourUsuarios } from '@renderer/tours';

export function UsuariosPage() {
  const [usuarios, setUsuarios] = useState<UsuarioVista[]>([]);
  const [editando, setEditando] = useState<UsuarioVista | null>(null);
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [cargando, setCargando] = useState(true);
  /** Sprint 64 — Los que ya no trabajan aquí se esconden por default. */
  const [verArchivados, setVerArchivados] = useState(false);

  const cargar = async () => {
    setCargando(true);
    const res = await window.api.usuarios.listar();
    if (res.ok) setUsuarios(res.data);
    setCargando(false);
  };

  useEffect(() => { cargar(); }, []);

  const archivados = usuarios.filter((u) => !u.activo);
  const visibles = verArchivados ? usuarios : usuarios.filter((u) => u.activo);
  /**
   * Sprint 64 — Quien no tiene PIN no puede entrar, y hasta ahora nada lo decía: el
   * negocio creó cajera y cocina, ninguna pudo entrar, y la pantalla de login sólo decía
   * "no disponible en la versión web".
   */
  const sinPin = usuarios.filter((u) => u.activo && u.activoLogin && u.tienePin === false);

  /**
   * Da de baja: el servidor borra de verdad si no hay historia y archiva si la hay. La
   * pantalla dice cuál de las dos pasó, porque no es lo mismo.
   */
  const onDarDeBaja = async (u: UsuarioVista) => {
    const confirmado = await window.confirmar({
      titulo: `¿Dar de baja a ${u.nombre}?`,
      mensaje:
        'Si nunca registró ventas se borra por completo. Si ya trabajó, se conserva su ' +
        'histórico y deja de aparecer en la lista y en el inicio de sesión.',
      textoConfirmar: 'Dar de baja',
      variant: 'destructive',
    });
    if (!confirmado) return;

    const res = await window.api.usuarios.eliminar(u.id);
    if (!res.ok) {
      window.alertar({ titulo: 'No se pudo', mensaje: res.error, variant: 'warn' });
      return;
    }
    window.alertar({
      titulo: res.data.borrado ? 'Usuario borrado' : 'Ya no trabaja aquí',
      mensaje: res.data.borrado
        ? `"${res.data.nombre}" se eliminó por completo: no tenía nada registrado.`
        : `"${res.data.nombre}" se archivó porque tiene ${res.data.historia.join(', ')}. ` +
          'Su histórico queda intacto y ya no aparece al iniciar sesión.',
      variant: 'info',
    });
    cargar();
  };

  const onReactivar = async (u: UsuarioVista) => {
    const res = await window.api.usuarios.reactivar(u.id);
    if (res.ok) cargar();
    else window.alertar({ titulo: 'No se pudo', mensaje: res.error, variant: 'warn' });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Usuarios</h1>
            <p className="text-sm text-slate-500">
              Quién trabaja aquí y qué puede hacer cada uno.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setEditando(null);
              setDrawerAbierto(true);
            }}
            className="btn-primary"
            data-tour="usr-nuevo"
          >
            <Plus className="h-4 w-4" />
            Nuevo usuario
          </button>
          <HelpButton tour={tourUsuarios} />
        </div>
      </div>

      {/*
        Sprint 64 — El toggle que pidió el negocio, con su contador.
        "En este ramo la rotación anual pasa del 100%": en dos años la lista va a tener
        veinte nombres de los que trabajan cinco. Esconderlos no es un caso borde, es cómo
        se va a ver la pantalla casi siempre — y el número dice qué se está escondiendo,
        para que nadie se pregunte si perdió a alguien.
      */}
      {archivados.length > 0 && (
        <label className="flex min-h-[44px] w-fit cursor-pointer items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={verArchivados}
            onChange={(e) => setVerArchivados(e.target.checked)}
          />
          Mostrar quienes ya no trabajan aquí ({archivados.length})
        </label>
      )}

      {sinPin.length > 0 && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3">
          <p className="text-sm font-semibold text-amber-900">
            {sinPin.length === 1
              ? `${sinPin[0].nombre} todavía no puede entrar`
              : `${sinPin.length} personas todavía no pueden entrar`}
          </p>
          <p className="mt-1 text-sm text-amber-800">
            Les falta un PIN. Ábrelas con el lápiz, marca{' '}
            <strong>“Asignarle un PIN para que pueda entrar”</strong> y teclea cuatro dígitos.
            Después cada quien puede cambiarlo desde <strong>Mi PIN</strong>.
          </p>
        </div>
      )}

      <div className="card overflow-hidden p-0" data-tour="usr-tabla">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Oficio</th>
              <th className="px-4 py-3">Situación</th>
              <th className="px-4 py-3">Último acceso</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cargando ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
            ) : visibles.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Sin usuarios.</td></tr>
            ) : (
              visibles.map((u) => (
                <tr key={u.id} className={`hover:bg-slate-50 ${!u.activo ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3 font-medium">{u.nombre}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`flex w-fit items-center gap-1 rounded px-2 py-0.5 text-xs ${
                        u.rol === 'dueño'
                          ? 'bg-purple-100 text-purple-700'
                          : u.rol === 'caja'
                            ? 'bg-emerald-100 text-emerald-700'
                            : u.rol === 'cocina'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                      }`}
                      /*
                        Sprint 94 — Si tiene puesto se muestra el puesto, pero el tooltip sigue
                        diciendo **qué puede hacer**: el permiso no se esconde detrás de una
                        etiqueta bonita. Quien administra usuarios tiene que poder ver que
                        "Consultor TI" alcanza todo lo que alcanza un dueño.
                      */
                      title={`${NOMBRE_ROL[u.rol as Rol] ?? u.rol} · ${DESCRIPCION_ROL[u.rol as Rol] ?? ''}`}
                    >
                      {u.rol === 'dueño' && <Shield className="h-3 w-3" />}
                      {u.puesto?.trim() || NOMBRE_ROL[u.rol as Rol] || u.rol}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {/*
                      Sprint 64 — En palabras. "Inactivo" no significa nada: nadie despide a
                      alguien "inactivándolo". Y el tercer estado —trabaja aquí pero no usa el
                      POS, sólo entra al reparto de propinas— existía desde el Sprint 12.1b y
                      la pantalla nunca lo dijo.
                    */}
                    {!u.activo ? (
                      <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-700">
                        Ya no trabaja aquí
                      </span>
                    ) : !u.activoLogin ? (
                      <span
                        className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
                        title="Trabaja aquí pero no usa el sistema; sí entra al reparto de propinas"
                      >
                        Sólo propinas
                      </span>
                    ) : (
                      <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        Trabaja aquí
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {u.ultimoLogin ? new Date(u.ultimoLogin).toLocaleString('es-MX') : 'nunca entró'}
                  </td>
                  <td className="px-4 py-3" data-tour="usr-acciones">
                    <div className="flex justify-end gap-1">
                      {u.activo ? (
                        <>
                          <button
                            onClick={() => { setEditando(u); setDrawerAbierto(true); }}
                            className="btn-ghost p-2"
                            aria-label={`Editar ${u.nombre}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => void onDarDeBaja(u)}
                            className="btn-ghost p-2 text-red-600"
                            aria-label={`Dar de baja a ${u.nombre}`}
                            title="Ya no trabaja aquí"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => void onReactivar(u)}
                          className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                        >
                          <UserCheck className="h-4 w-4" />
                          Volvió a trabajar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <FormUsuario
        abierto={drawerAbierto}
        usuario={editando}
        onClose={() => setDrawerAbierto(false)}
        onSaved={() => {
          setDrawerAbierto(false);
          cargar();
        }}
      />
    </div>
  );
}
